import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import {
  RichTableBlockModel,
  type RichTableCellBlockModel,
} from '@blocksuite/affine-model';
import { matchModels } from '@blocksuite/affine-shared/utils';
import {
  ArrowDownBigIcon,
  ArrowLeftBigIcon,
  ArrowRightBigIcon,
  ArrowUpBigIcon,
  DeleteIcon,
  InsertLeftIcon,
  InsertRightIcon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
} from '@blocksuite/icons/lit';
import { BlockComponent } from '@blocksuite/std';
import { html, nothing } from 'lit';

import {
  deleteColumn,
  deleteRow,
  insertColumn,
  insertRow,
  moveColumn,
  moveRow,
} from './commands/table-ops.js';
import type { RichTableBlockComponent } from './rich-table-block.js';
import {
  columnHandleStyles,
  reorderingSourceStyles,
  richTableCellStyles,
  richTableHeaderCellStyles,
  rowHandleStyles,
} from './styles.js';

export class RichTableCellBlockComponent extends BlockComponent<RichTableCellBlockModel> {
  /** Column index of this cell within its row. */
  get columnIndex() {
    return this.model.parent?.children.indexOf(this.model) ?? -1;
  }

  /** Row index of this cell's row within the table. */
  get rowIndex() {
    const row = this.model.parent;
    return row?.parent?.children.indexOf(row) ?? -1;
  }

  get table() {
    const table = this.model.parent?.parent;
    return matchModels(table, [RichTableBlockModel]) ? table : null;
  }

  private get _tableComponent() {
    return this.closest<RichTableBlockComponent>('affine-rich-table');
  }

  private get _isHeaderCell() {
    const table = this.table;
    if (!table?.props.headerRow) return false;
    return table.children[0]?.id === this.model.parent?.id;
  }

  /**
   * Every cell owns a resizer on its right edge, the last one included.
   * Columns are absolute widths, so a drag moves that one boundary and the
   * table gets wider or narrower — neighbours are never disturbed.
   */
  private get _hasResizer() {
    return this.columnIndex > -1;
  }

  private _popColumnMenu(target: HTMLElement) {
    const table = this.table;
    const index = this.columnIndex;
    if (!table || index < 0) return;
    const store = this.store;

    popMenu(popupTargetFromElement(target), {
      options: {
        items: [
          menu.group({
            items: [
              menu.action({
                name: 'Insert left',
                prefix: InsertLeftIcon(),
                select: () => insertColumn(store, table, index),
              }),
              menu.action({
                name: 'Insert right',
                prefix: InsertRightIcon(),
                select: () => insertColumn(store, table, index + 1),
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Move left',
                prefix: ArrowLeftBigIcon(),
                select: () => moveColumn(store, table, index, index - 1),
              }),
              menu.action({
                name: 'Move right',
                prefix: ArrowRightBigIcon(),
                select: () => moveColumn(store, table, index, index + 1),
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Delete column',
                class: { 'delete-item': true },
                prefix: DeleteIcon(),
                select: () => deleteColumn(store, table, index),
              }),
            ],
          }),
        ],
      },
    });
  }

  private _popRowMenu(target: HTMLElement) {
    const table = this.table;
    const index = this.rowIndex;
    if (!table || index < 0) return;
    const store = this.store;

    popMenu(popupTargetFromElement(target), {
      options: {
        items: [
          menu.group({
            items: [
              menu.action({
                name: 'Insert above',
                prefix: InsertLeftIcon(),
                select: () => insertRow(store, table, index),
              }),
              menu.action({
                name: 'Insert below',
                prefix: InsertRightIcon(),
                select: () => insertRow(store, table, index + 1),
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Move up',
                prefix: ArrowUpBigIcon(),
                select: () => moveRow(store, table, index, index - 1),
              }),
              menu.action({
                name: 'Move down',
                prefix: ArrowDownBigIcon(),
                select: () => moveRow(store, table, index, index + 1),
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Delete row',
                class: { 'delete-item': true },
                prefix: DeleteIcon(),
                select: () => deleteRow(store, table, index),
              }),
            ],
          }),
        ],
      },
    });
  }

  /**
   * Drags a handle to reorder. A drag under this threshold is treated as a
   * click and opens the menu instead, so both gestures live on one control.
   */
  private _startReorder(
    event: PointerEvent,
    axis: 'column' | 'row',
    openMenu: (target: HTMLElement) => void
  ) {
    if (event.button !== 0) return;
    const table = this.table;
    const host = this._tableComponent;
    const from = axis === 'column' ? this.columnIndex : this.rowIndex;
    if (!table || !host || from < 0 || this.store.readonly) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const startY = event.clientY;
    let moved = false;

    const fromSignal =
      axis === 'column' ? host.reorderColumnFrom$ : host.reorderRowFrom$;
    const toSignal =
      axis === 'column' ? host.reorderColumnTo$ : host.reorderRowTo$;

    const onMove = (move: PointerEvent) => {
      if (
        !moved &&
        Math.hypot(move.clientX - startX, move.clientY - startY) < 4
      ) {
        return;
      }
      if (!moved) {
        moved = true;
        fromSignal.value = from;
      }
      toSignal.value = this._indexAtPoint(axis, move);
    };

    const onUp = (up: PointerEvent) => {
      handle.releasePointerCapture(up.pointerId);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);

      const to = toSignal.value;
      fromSignal.value = null;
      toSignal.value = null;

      if (!moved) {
        openMenu(handle);
        return;
      }
      if (to === null || to === from) return;
      if (axis === 'column') moveColumn(this.store, table, from, to);
      else moveRow(this.store, table, from, to);
    };

    handle.setPointerCapture(event.pointerId);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  /** Which column/row the pointer is over, by hit-testing the live cells. */
  private _indexAtPoint(axis: 'column' | 'row', event: PointerEvent) {
    const host = this._tableComponent;
    if (!host) return null;

    if (axis === 'column') {
      const row = host.querySelector('affine-rich-table-row');
      const cells = [...(row?.children ?? [])];
      for (let i = 0; i < cells.length; i++) {
        const rect = cells[i].getBoundingClientRect();
        if (event.clientX < rect.right) return i;
      }
      return cells.length - 1;
    }

    const rows = [...host.querySelectorAll('affine-rich-table-row')];
    for (let i = 0; i < rows.length; i++) {
      const rect = rows[i].getBoundingClientRect();
      if (event.clientY < rect.bottom) return i;
    }
    return rows.length - 1;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(richTableCellStyles);
  }

  override renderBlock() {
    this.classList.toggle(richTableHeaderCellStyles, this._isHeaderCell);

    const host = this._tableComponent;
    this.classList.toggle(
      reorderingSourceStyles,
      host?.reorderColumnFrom$.value === this.columnIndex ||
        host?.reorderRowFrom$.value === this.rowIndex
    );
    const readonly = this.store.readonly;
    // The handles belong to the first row / first column, but light up from a
    // hover anywhere in that column / row. A handle being dragged also has to
    // stay mounted once the pointer leaves its own column: unmounting it would
    // drop the pointer capture and kill the drag.
    const showColumnHandle =
      !readonly &&
      this.rowIndex === 0 &&
      (host?.hoverColumn$.value === this.columnIndex ||
        host?.reorderColumnFrom$.value === this.columnIndex);
    const showRowHandle =
      !readonly &&
      this.columnIndex === 0 &&
      (host?.hoverRow$.value === this.rowIndex ||
        host?.reorderRowFrom$.value === this.rowIndex);

    return html`
      ${
        showColumnHandle
          ? html`<div
              data-testid="rich-table-column-handle"
              class=${columnHandleStyles}
              @pointerdown=${(event: PointerEvent) =>
                this._startReorder(event, 'column', target =>
                  this._popColumnMenu(target)
                )}
            >
              ${MoreHorizontalIcon()}
            </div>`
          : nothing
      }
      ${
        showRowHandle
          ? html`<div
              data-testid="rich-table-row-handle"
              class=${rowHandleStyles}
              @pointerdown=${(event: PointerEvent) =>
                this._startReorder(event, 'row', target =>
                  this._popRowMenu(target)
                )}
            >
              ${MoreVerticalIcon()}
            </div>`
          : nothing
      }
      ${
        this._hasResizer
          ? html`<affine-rich-table-resizer
              .cell=${this}
            ></affine-rich-table-resizer>`
          : nothing
      }
      <div class="affine-block-children-container">
        ${this.renderChildren(this.model)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-rich-table-cell': RichTableCellBlockComponent;
  }
}
